<template>
  <div class="landing-page-bright">
    <!-- Main Navigation Header -->
    <header class="main-header" :class="{ 'scrolled': isScrolled }">
      <div class="header-inner">
        <!-- Logo -->
        <router-link to="/" class="brand-logo-link">
          <div class="logo-mark">
            <svg viewBox="0 0 100 100" class="diamond-svg">
              <polygon points="50,12 85,38 50,88 15,38" fill="none" stroke="#9E6D2B" stroke-width="5" stroke-linejoin="round" />
              <line x1="15" y1="38" x2="85" y2="38" stroke="#9E6D2B" stroke-width="4" />
              <polyline points="32,12 28,38 50,88 72,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" />
              <polyline points="32,12 50,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" />
            </svg>
          </div>
          <div class="logo-text-block">
            <div class="brand-name">JEWEL<span class="brand-highlight">BIZ</span></div>
            <div class="brand-subtext">SMART JEWELLERY BUSINESS MANAGEMENT</div>
          </div>
        </router-link>

        <!-- Desktop Navigation Links -->
        <nav class="desktop-nav d-none d-lg-flex">
          <a href="#features" class="nav-link">Features</a>
          <a href="#modules" class="nav-link">ERP Modules</a>
          <a href="#karigar" class="nav-link">Karigar Ledger</a>
          <a href="#schemes" class="nav-link">Gold Schemes</a>
          <a href="#why-jewelbiz" class="nav-link">Why JewelBiz</a>
          <a href="#faq" class="nav-link">FAQ</a>
        </nav>

        <!-- Right Header Actions -->
        <div class="header-actions">
          <button class="btn-demo-outline d-none d-sm-inline-flex" @click="bookDemoDialog = true">
            <v-icon small class="mr-1 btn-icon">mdi-calendar-check-outline</v-icon>
            Book a Demo
          </button>
          <router-link to="/login" class="btn-signin-gold">
            Sign In
            <v-icon small class="ml-1 btn-icon">mdi-arrow-right</v-icon>
          </router-link>
          <!-- Mobile Menu Toggle -->
          <button class="mobile-toggle-btn d-lg-none" @click="mobileDrawer = !mobileDrawer">
            <v-icon>mdi-menu</v-icon>
          </button>
        </div>
      </div>
    </header>

    <!-- Mobile Drawer -->
    <v-navigation-drawer v-model="mobileDrawer" fixed temporary right class="mobile-nav-drawer pa-4">
      <div class="d-flex justify-space-between align-center mb-6">
        <div class="brand-name font-weight-bold">JEWEL<span class="brand-highlight">BIZ</span></div>
        <v-btn icon @click="mobileDrawer = false"><v-icon>mdi-close</v-icon></v-btn>
      </div>
      <div class="mobile-nav-links">
        <a href="#features" @click="mobileDrawer = false">Features</a>
        <a href="#modules" @click="mobileDrawer = false">ERP Modules</a>
        <a href="#karigar" @click="mobileDrawer = false">Karigar Ledger</a>
        <a href="#schemes" @click="mobileDrawer = false">Gold Schemes</a>
        <a href="#why-jewelbiz" @click="mobileDrawer = false">Why JewelBiz</a>
        <a href="#faq" @click="mobileDrawer = false">FAQ</a>
        <a href="javascript:void(0)" @click="mobileDrawer = false; openLegalModal('privacy')">Privacy Policy</a>
        <a href="javascript:void(0)" @click="mobileDrawer = false; openLegalModal('terms')">Terms & Conditions</a>
      </div>
      <div class="mt-8">
        <v-btn block color="#9E6D2B" dark large rounded class="mb-3" @click="mobileDrawer = false; bookDemoDialog = true">
          Book a Live Demo
        </v-btn>
        <v-btn block outlined color="#9E6D2B" large rounded to="/login">
          Sign In to Portal
        </v-btn>
      </div>
    </v-navigation-drawer>

    <!-- Main Content -->
    <main class="main-body">
      <!-- 1. Hero Section (Banner Showcase) -->
      <section class="hero-showcase">
        <div class="hero-container">
          <div class="hero-grid">
            <!-- Left Text Content -->
            <div class="hero-text-side">
              <div class="eyebrow-line">
                <span class="eyebrow-text">ALL-IN-ONE JEWELLERY ERP PLATFORM</span>
                <span class="eyebrow-bar"></span>
              </div>
              <h1 class="hero-headline">
                Manage. Grow.<br>
                <span class="headline-serif-gold">Shine with JewelBiz.</span>
              </h1>
              <p class="hero-subline">
                Streamline billing, barcode inventory, karigar workshop tracking, gold savings schemes, and GST compliance all in one smart cloud platform designed for jewellery retail showrooms and wholesalers.
              </p>

              <!-- Hero CTAs -->
              <div class="hero-button-group">
                <button class="hero-btn-primary" @click="bookDemoDialog = true">
                  Book a Free Demo <span class="arrow-symbol">→</span>
                </button>
                <a href="#features" class="hero-btn-outline">
                  <span class="play-icon-circle">✦</span> Explore ERP Features
                </a>
              </div>

              <!-- Trust Badges -->
              <div class="hero-trust-row">
                <div class="trust-item">
                  <v-icon class="trust-icon" size="20">mdi-shield-check-outline</v-icon>
                  <span class="trust-label">100% GST & HUID Ready</span>
                </div>
                <div class="trust-item">
                  <v-icon class="trust-icon" size="20">mdi-cloud-lock-outline</v-icon>
                  <span class="trust-label">Role-Based Cloud Security</span>
                </div>
                <div class="trust-item">
                  <v-icon class="trust-icon" size="20">mdi-sync</v-icon>
                  <span class="trust-label">Automated Daily Rates</span>
                </div>
              </div>
            </div>

            <!-- Right Visual Display Carousel -->
            <div class="hero-visual-side" @mouseenter="pauseHeroTimer" @mouseleave="startHeroTimer">
              <div class="hero-visual-card">
                <div
                  v-for="(slide, sIndex) in heroSlides"
                  :key="slide.id"
                  class="hero-slide-item"
                  :class="{ 'active': currentHeroSlide === (sIndex + 1) }"
                >
                  <img
                    :src="slide.image"
                    :alt="slide.caption"
                    class="hero-showcase-img"
                  />
                </div>
                <div class="crafted-caption">
                  <span>{{ heroSlides[currentHeroSlide - 1].subcaption }}</span>
                  <div class="caption-line"></div>
                </div>
              </div>

              <!-- Carousel Pagination Controls -->
              <div class="hero-carousel-nav">
                <button class="carousel-arrow" @click="prevHeroSlide" aria-label="Previous Slide">‹</button>
                <div class="carousel-dots">
                  <span
                    v-for="(slide, sIdx) in heroSlides"
                    :key="sIdx"
                    class="carousel-dot"
                    :class="{ 'active': currentHeroSlide === (sIdx + 1) }"
                    @click="goToHeroSlide(sIdx + 1)"
                  ></span>
                </div>
                <button class="carousel-arrow" @click="nextHeroSlide" aria-label="Next Slide">›</button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 2. Circular Pillar Navigation (8 Core ERP Modules) -->
      <section class="category-circles-section">
        <div class="circles-container">
          <div class="circles-track">
            <div
              v-for="(mod, idx) in erpPillars"
              :key="idx"
              class="circle-card-item"
              @click="openModuleDetail(mod)"
            >
              <div class="circle-avatar-frame">
                <img :src="mod.image" :alt="mod.title" class="circle-item-img" />
              </div>
              <span class="circle-item-title">{{ mod.title }}</span>
            </div>
          </div>
        </div>
      </section>

      <!-- 3. Section: "COMPREHENSIVE ERP MODULES" (4 Showcase Cards) -->
      <section id="modules" class="exclusive-collections-section">
        <div class="section-header-wrap">
          <div class="section-kicker">ENTERPRISE SOLUTIONS</div>
          <h2 class="section-main-title">Complete Jewellery Business Suite</h2>
          <p class="section-desc">Four core systems working in harmony to eliminate leakages and automate daily showroom operations</p>
          <a href="#features" class="view-all-link">View All Features →</a>
        </div>

        <div class="collections-grid-container">
          <div class="collections-grid">
            <div
              v-for="(col, i) in erpModules"
              :key="i"
              class="collection-card"
              @click="openModuleDetail(col)"
            >
              <div class="collection-img-wrap">
                <img :src="col.image" :alt="col.title" class="collection-img" />
                <div class="collection-tag">{{ col.tag }}</div>
              </div>
              <div class="collection-card-body">
                <h3 class="collection-title">{{ col.title }}</h3>
                <p class="collection-subtitle">{{ col.subtitle }}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- 4. Section: "WHY CHOOSE JEWELBIZ / Built for the Jewellery Trade" -->
      <section id="why-jewelbiz" class="why-choose-section">
        <div class="section-header-wrap text-center">
          <div class="section-kicker">WHY CHOOSE JEWELBIZ</div>
          <h2 class="section-main-title">Built Exclusively for the Jewellery Trade</h2>
          <p class="section-desc">Designed to overcome the unique operational, audit, and tax challenges faced by retail jewellers</p>
        </div>

        <div class="why-cards-container">
          <div class="why-cards-grid">
            <div
              v-for="(card, k) in whyCards"
              :key="k"
              class="why-card"
            >
              <div class="why-icon-box">
                <v-icon size="32" class="why-gold-icon">{{ card.icon }}</v-icon>
              </div>
              <h3 class="why-card-title">{{ card.title }}</h3>
              <p class="why-card-desc">{{ card.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 5. Promotional Occasion Banner -->
      <section class="occasion-banner-section">
        <div class="occasion-banner-wrapper">
          <div class="occasion-banner-inner">
            <!-- Left Info -->
            <div class="occasion-text-wrap">
              <h2 class="occasion-heading">Ready to Modernize Your Jewellery Business?</h2>
              <p class="occasion-subtext">
                Join leading jewellery retailers who have replaced manual paper registers with JewelBiz. Automate billing, secure your stock, and scale your showroom with confidence.
              </p>
              <button class="occasion-cta-btn" @click="bookDemoDialog = true">
                Book a Free Live Demo →
              </button>
            </div>

            <!-- Right Visual Banner Image -->
            <div class="occasion-img-wrap">
              <img
                src="/landing/banner_wedding_bangles.jpg"
                alt="Fine Bridal Jewellery Management"
                class="occasion-bangles-img"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- 6. Deep Capabilities & Key Features Section -->
      <section id="features" class="deep-features-section">
        <div class="section-header-wrap text-center">
          <div class="section-kicker">POWERFUL SUITE</div>
          <h2 class="section-main-title">Core Management Features</h2>
          <p class="section-desc">Everything needed to operate your sales counters, warehouse stock, and artisan network</p>
        </div>

        <div class="features-grid-container">
          <div class="features-grid">
            <div v-for="(feat, fIdx) in detailedFeatures" :key="fIdx" class="feature-tile">
              <div class="feature-tile-icon-wrap">
                <v-icon size="26" class="feature-tile-icon">{{ feat.icon }}</v-icon>
              </div>
              <h4 class="feature-tile-title">{{ feat.title }}</h4>
              <p class="feature-tile-desc">{{ feat.desc }}</p>
            </div>
          </div>
        </div>
      </section>

      <!-- 7. Karigar & Workshop Spotlight -->
      <section id="karigar" class="spotlight-section">
        <div class="spotlight-container">
          <div class="spotlight-grid">
            <div class="spotlight-text">
              <div class="section-kicker">WORKSHOP INTEGRITY</div>
              <h2 class="section-main-title">Karigar & Melting Ledger</h2>
              <p class="spotlight-paragraph">
                JewelBiz eliminates weight discrepancies and unaccounted melting loss when dispatching gold to artisans. Every bar, scrap, and stone is audited from raw material to finished showroom ornament.
              </p>
              <ul class="spotlight-list">
                <li><v-icon small class="mr-2 check-icon">mdi-check-decagram</v-icon> Real-time artisan job slip tracking & raw metal issue</li>
                <li><v-icon small class="mr-2 check-icon">mdi-check-decagram</v-icon> Exact melting loss & waste fraction calculations</li>
                <li><v-icon small class="mr-2 check-icon">mdi-check-decagram</v-icon> Customer repair jobs with photo tags and stone weights</li>
              </ul>
              <button class="btn-demo-outline mt-4" @click="bookDemoDialog = true">
                See Karigar Module Live →
              </button>
            </div>
            <div class="spotlight-visual">
              <img src="/landing/circle_bracelets.jpg" alt="Artisan Craftsmanship" class="spotlight-img" />
            </div>
          </div>
        </div>
      </section>

      <!-- 8. Gold Schemes Spotlight -->
      <section id="schemes" class="spotlight-section bg-alt">
        <div class="spotlight-container">
          <div class="spotlight-grid reverse-on-desktop">
            <div class="spotlight-visual">
              <img src="/landing/circle_necklaces.jpg" alt="Gold Savings Schemes" class="spotlight-img" />
            </div>
            <div class="spotlight-text">
              <div class="section-kicker">RECURRING REVENUE</div>
              <h2 class="section-main-title">Automated Gold Schemes</h2>
              <p class="spotlight-paragraph">
                Lock in customer loyalty and recurring gold purchases through digitized 11-month and 12-month jewellery savings plans. Automate digital passbooks, payment alerts, and bonus maturity calculations effortlessly.
              </p>
              <ul class="spotlight-list">
                <li><v-icon small class="mr-2 check-icon">mdi-check-decagram</v-icon> Digital customer scheme passbooks & ledger balance</li>
                <li><v-icon small class="mr-2 check-icon">mdi-check-decagram</v-icon> Automated installment reminders via WhatsApp and SMS</li>
                <li><v-icon small class="mr-2 check-icon">mdi-check-decagram</v-icon> Instant scheme redemption against live daily gold rates</li>
              </ul>
              <button class="btn-demo-outline mt-4" @click="bookDemoDialog = true">
                Explore Scheme Module →
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- 9. FAQ Section -->
      <section id="faq" class="faq-bright-section">
        <div class="section-header-wrap text-center">
          <div class="section-kicker">QUESTIONS & ANSWERS</div>
          <h2 class="section-main-title">Frequently Asked Questions</h2>
          <p class="section-desc">Everything you need to know about implementing JewelBiz in your jewellery store</p>
        </div>

        <div class="faq-container">
          <v-expansion-panels flat class="faq-panels-bright">
            <v-expansion-panel v-for="(faq, fq) in faqs" :key="fq" class="faq-panel-bright">
              <v-expansion-panel-header class="faq-header-bright">
                {{ faq.question }}
              </v-expansion-panel-header>
              <v-expansion-panel-content class="faq-content-bright">
                {{ faq.answer }}
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
        </div>
      </section>

      <!-- 10. Bottom Callout Section -->
      <section class="bottom-callout-section">
        <div class="callout-card">
          <div class="callout-ornament">✦</div>
          <h2 class="callout-title">Ready to Elevate Your Jewellery Business?</h2>
          <p class="callout-desc">
            Experience why hundreds of leading jewellery retail stores and wholesalers trust JewelBiz for their daily billing, inventory, and accounts.
          </p>
          <div class="callout-actions">
            <button class="btn-callout-primary" @click="bookDemoDialog = true">
              Schedule Your Free Demo →
            </button>
            <router-link to="/login" class="btn-callout-secondary">
              Sign In to Dashboard
            </router-link>
          </div>
        </div>
      </section>
    </main>

    <!-- 11. Bright Luxury Footer -->
    <footer class="bright-footer">
      <div class="footer-container">
        <div class="footer-top-row">
          <!-- Col 1: Brand -->
          <div class="footer-col brand-col">
            <div class="brand-logo-link mb-3">
              <div class="logo-mark">
                <svg viewBox="0 0 100 100" class="diamond-svg">
                  <polygon points="50,12 85,38 50,88 15,38" fill="none" stroke="#9E6D2B" stroke-width="5" stroke-linejoin="round" stroke-linecap="round" />
                  <line x1="15" y1="38" x2="85" y2="38" stroke="#9E6D2B" stroke-width="4" stroke-linecap="round" />
                  <polyline points="32,12 28,38 50,88 72,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />
                  <polyline points="32,12 50,38 68,12" fill="none" stroke="#9E6D2B" stroke-width="4" stroke-linejoin="round" stroke-linecap="round" />
                </svg>
              </div>
              <div class="logo-text-block">
                <div class="brand-name">JEWEL<span class="brand-highlight">BIZ</span></div>
                <div class="brand-subtext">SMART JEWELLERY BUSINESS MANAGEMENT</div>
              </div>
            </div>
            <p class="footer-brand-desc">
              JewelBiz empowers retail jewellers and wholesalers with end-to-end digital inventory, GST billing, karigar management, and live gold rate synchronization.
            </p>
          </div>

          <!-- Col 2: Navigation Links -->
          <div class="footer-col">
            <h4 class="footer-col-title">Platform</h4>
            <ul class="footer-links">
              <li><a href="#features">Features</a></li>
              <li><a href="#modules">ERP Modules</a></li>
              <li><a href="#karigar">Karigar & Repair</a></li>
              <li><a href="#schemes">Gold Savings Schemes</a></li>
              <li><a href="#why-jewelbiz">Why Choose Us</a></li>
            </ul>
          </div>

          <!-- Col 3: Modules -->
          <div class="footer-col">
            <h4 class="footer-col-title">Solutions</h4>
            <ul class="footer-links">
              <li><a href="#modules">GST Invoicing & HUID</a></li>
              <li><a href="#modules">Stock Valuation</a></li>
              <li><a href="#modules">Daily Boarding Rates</a></li>
              <li><a href="#modules">Customer Statements</a></li>
              <li><router-link to="/login">Store Portal Login</router-link></li>
            </ul>
          </div>

          <!-- Col 4: Legal & Policies -->
          <div class="footer-col">
            <h4 class="footer-col-title">Legal & Policies</h4>
            <ul class="footer-links">
              <li>
                <button type="button" class="footer-link-action" @click="openLegalModal('privacy')">
                  Privacy Policy
                </button>
              </li>
              <li>
                <button type="button" class="footer-link-action" @click="openLegalModal('terms')">
                  Terms & Conditions
                </button>
              </li>
              <li>
                <router-link to="/privacy-policy" target="_blank">Data Protection Doc ↗</router-link>
              </li>
              <li>
                <router-link to="/terms-conditions" target="_blank">Commercial Terms Doc ↗</router-link>
              </li>
              <li><a href="#why-jewelbiz">ISO 27001 Security</a></li>
            </ul>
          </div>

          <!-- Col 5: Contact -->
          <div class="footer-col">
            <h4 class="footer-col-title">Contact & Support</h4>
            <ul class="footer-links contact-links">
              <li>
                <v-icon small class="mr-2 footer-contact-icon">mdi-phone</v-icon>
                <a href="tel:+919633845820">+91 9633845820</a>
              </li>
              <li>
                <v-icon small class="mr-2 footer-contact-icon">mdi-email-outline</v-icon>
                <a href="mailto:support@jewelbiz.com">support@jewelbiz.com</a>
              </li>
              <li>
                <v-icon small class="mr-2 footer-contact-icon">mdi-map-marker-outline</v-icon>
                <span>Kochi, Kerala, India</span>
              </li>
            </ul>
            <button class="btn-demo-outline btn-sm-footer mt-3" @click="bookDemoDialog = true">
              Schedule Consultation
            </button>
          </div>
        </div>

        <div class="footer-bottom-bar">
          <div class="footer-legal-bar-links">
            <button type="button" class="footer-legal-btn" @click="openLegalModal('privacy')">Privacy Policy</button>
            <span class="legal-dot">•</span>
            <button type="button" class="footer-legal-btn" @click="openLegalModal('terms')">Terms & Conditions</button>
            <span class="legal-dot">•</span>
            <router-link to="/privacy-policy" class="footer-legal-btn">Full Policy</router-link>
            <span class="legal-dot">•</span>
            <router-link to="/terms-conditions" class="footer-legal-btn">Full Terms</router-link>
          </div>
          <div class="footer-bottom-badges">
            <span class="cert-badge">✦ ISO 27001 Security Standard</span>
            <span class="cert-badge">✦ 100% Indian GST & HUID Ready</span>
          </div>
        </div>
      </div>
    </footer>

    <!-- Book a Demo Dialog (Connected to /user/bookdemo API) -->
    <!-- Book a Demo Dialog (Connected to /user/bookdemo API) -->
    <v-dialog
      v-model="bookDemoDialog"
      max-width="580px"
      content-class="luxury-demo-dialog-clean"
    >
      <div class="clean-demo-card">
        <!-- Gold Accent Top Bar -->
        <div class="gold-top-accent"></div>

        <!-- Header -->
        <div class="clean-modal-header">
          <div>
            <div class="clean-gold-kicker">EXPERIENCE JEWELBIZ</div>
            <h3 class="clean-modal-title">Book a Live Demo</h3>
            <p class="clean-modal-subtitle">Personalized walkthrough for your jewellery showroom operations.</p>
          </div>
          <button
            type="button"
            class="clean-close-btn"
            @click="bookDemoDialog = false"
            aria-label="Close dialog"
          >
            <v-icon color="#9E6D2B">mdi-close</v-icon>
          </button>
        </div>

        <!-- Form -->
        <v-form ref="demoForm" v-model="demoFormValid" lazy-validation class="clean-modal-body">
          <div class="clean-form-grid">
            <div class="clean-form-group">
              <label class="clean-field-label">Your Name <span class="required-star">*</span></label>
              <v-text-field
                v-model="booking.name"
                :rules="[v => !!v || 'Name is required']"
                placeholder="Enter your name"
                outlined
                dense
                hide-details="auto"
                class="clean-field-input"
              ></v-text-field>
            </div>

            <div class="clean-form-group">
              <label class="clean-field-label">Jewellery Shop Name <span class="required-star">*</span></label>
              <v-text-field
                v-model="booking.jewelleryName"
                :rules="[v => !!v || 'Shop name is required']"
                placeholder="Business / Showroom name"
                outlined
                dense
                hide-details="auto"
                class="clean-field-input"
              ></v-text-field>
            </div>
          </div>

          <div class="clean-form-grid">
            <div class="clean-form-group">
              <label class="clean-field-label">Email Address <span class="required-star">*</span></label>
              <v-text-field
                v-model="booking.email"
                :rules="[
                  v => !!v || 'Email is required',
                  v => /.+@.+\..+/.test(v) || 'Enter valid email'
                ]"
                placeholder="you@jewellers.com"
                outlined
                dense
                hide-details="auto"
                class="clean-field-input"
              ></v-text-field>
            </div>

            <div class="clean-form-group">
              <label class="clean-field-label">Phone Number <span class="required-star">*</span></label>
              <v-text-field
                v-model="booking.phone"
                :rules="[
                  v => !!v || 'Phone is required',
                  v => /^\d{10,}$/.test(v) || 'Enter valid 10-digit number'
                ]"
                placeholder="10-digit mobile number"
                outlined
                dense
                hide-details="auto"
                class="clean-field-input"
              ></v-text-field>
            </div>
          </div>

          <div class="clean-form-grid">
            <div class="clean-form-group">
              <label class="clean-field-label">Preferred Date <span class="required-star">*</span></label>
              <v-text-field
                v-model="booking.preferredDate"
                :rules="[v => !!v || 'Date is required']"
                :min="todayDate"
                type="date"
                outlined
                dense
                hide-details="auto"
                class="clean-field-input"
              ></v-text-field>
            </div>

            <div class="clean-form-group">
              <label class="clean-field-label">Preferred Time Slot <span class="required-star">*</span></label>
              <v-select
                v-model="booking.preferredTime"
                :items="['Morning (9 AM - 12 PM)', 'Afternoon (12 PM - 4 PM)', 'Evening (4 PM - 7 PM)']"
                :rules="[v => !!v || 'Time slot is required']"
                placeholder="Select time"
                outlined
                dense
                hide-details="auto"
                class="clean-field-input"
              ></v-select>
            </div>
          </div>

          <div class="clean-form-group mb-2">
            <label class="clean-field-label">Specific Needs (Optional)</label>
            <v-textarea
              v-model="booking.notes"
              placeholder="E.g., Multi-branch sync, karigar tracking, scheme automation, old gold billing..."
              outlined
              dense
              rows="2"
              hide-details="auto"
              class="clean-field-input"
            ></v-textarea>
          </div>

          <button
            type="button"
            class="clean-submit-btn"
            :disabled="bookingLoading"
            @click="submitBooking()"
          >
            <span v-if="bookingLoading" class="clean-btn-spinner"></span>
            <span v-else class="clean-btn-text">
              Submit Demo Request
              <v-icon small class="ml-1" color="#FFFFFF">mdi-arrow-right</v-icon>
            </span>
          </button>
        </v-form>
      </div>
    </v-dialog>

    <!-- Detail Item Modal (For Pillars or Modules) -->
    <v-dialog v-model="detailModal" max-width="520px">
      <v-card v-if="selectedItem" class="bright-modal-card pa-6">
        <div class="d-flex justify-space-between align-center mb-3">
          <h3 class="modal-heading">{{ selectedItem.title }}</h3>
          <v-btn icon @click="detailModal = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
        <div class="detail-modal-img-wrap mb-4">
          <img :src="selectedItem.image" :alt="selectedItem.title" class="detail-modal-img" />
        </div>
        <p class="detail-modal-desc mb-4">{{ selectedItem.desc || selectedItem.subtitle }}</p>
        <button class="hero-btn-primary w-100" @click="detailModal = false; bookDemoDialog = true">
          Request Live Module Demo →
        </button>
      </v-card>
    </v-dialog>

    <!-- Privacy Policy & Terms & Conditions Interactive Modal -->
    <v-dialog
      v-model="legalDialog"
      max-width="840px"
      content-class="luxury-legal-dialog"
      scrollable
    >
      <div class="clean-legal-card">
        <!-- Gold Top Accent -->
        <div class="gold-top-accent"></div>

        <!-- Header -->
        <div class="clean-legal-header">
          <div>
            <div class="clean-gold-kicker">LEGAL & COMPLIANCE</div>
            <h3 class="clean-legal-title">
              {{ legalTab === 'privacy' ? 'Privacy Policy' : 'Terms & Conditions' }}
            </h3>
            <p class="clean-legal-subtitle">
              {{ legalTab === 'privacy' 
                 ? 'How JewelBiz protects and handles your showroom, inventory, and customer data.'
                 : 'Subscription terms, licensing, and operational guidelines for JewelBiz ERP platform.' }}
            </p>
          </div>
          <button
            type="button"
            class="clean-close-btn"
            @click="legalDialog = false"
            aria-label="Close dialog"
          >
            <v-icon color="#9E6D2B">mdi-close</v-icon>
          </button>
        </div>

        <!-- Tab Selector Switcher -->
        <div class="legal-tab-switcher">
          <button 
            type="button"
            class="legal-tab-btn" 
            :class="{ 'active': legalTab === 'privacy' }"
            @click="legalTab = 'privacy'"
          >
            <v-icon small class="mr-1" :color="legalTab === 'privacy' ? '#FFFFFF' : '#9E6D2B'">mdi-shield-lock-outline</v-icon>
            Privacy Policy
          </button>
          <button 
            type="button"
            class="legal-tab-btn" 
            :class="{ 'active': legalTab === 'terms' }"
            @click="legalTab = 'terms'"
          >
            <v-icon small class="mr-1" :color="legalTab === 'terms' ? '#FFFFFF' : '#9E6D2B'">mdi-file-document-edit-outline</v-icon>
            Terms & Conditions
          </button>
        </div>

        <!-- Scrollable Modal Content -->
        <div class="legal-modal-scroll-area">
          <!-- PRIVACY POLICY CONTENT -->
          <div v-if="legalTab === 'privacy'" class="legal-modal-inner">
            <div class="modal-legal-badge-row">
              <span class="meta-pill-sm"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-shield-check</v-icon> ISO 27001 Security Standard</span>
              <span class="meta-pill-sm"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-calendar-check</v-icon> Updated: Sept 2026</span>
              <span class="meta-pill-sm"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-lock-outline</v-icon> 256-Bit SSL/TLS Encryption</span>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">1. Overview & Scope</h4>
              <p>
                <strong>JewelBiz</strong> provides enterprise-grade jewellery software solutions for retail showrooms, wholesalers, and manufacturers. We are committed to safeguarding your business data, stock secrecy, and customer privacy.
              </p>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">2. Information We Collect</h4>
              <ul class="modal-legal-list">
                <li><strong>Showroom & Business Profile:</strong> Trade name, branch locations, GSTIN, BIS Hallmark HUID licenses, contact info, and tax profiles.</li>
                <li><strong>Customer & Scheme Ledgers:</strong> Customer contact info, gold savings scheme enrollments, monthly passbook transaction logs, and bonus redemption records.</li>
                <li><strong>Inventory & Karigar Balances:</strong> Karigar metal ledger balances, gross/net weights, purity (24K, 22K/916, 18K, Silver, Platinum), wastage (V.A.), and stone specifications.</li>
                <li><strong>System & Audit Logs:</strong> Encrypted user credentials, role permissions, login timestamps, and transaction audit trails.</li>
              </ul>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">3. 100% Tenant Data Ownership</h4>
              <div class="modal-callout-box">
                <v-icon small color="#9E6D2B" class="mr-2">mdi-check-decagram</v-icon>
                <span><strong>Your Data Belongs Exclusively to You:</strong> We never sell, lease, rent, or share your customer records, margins, or stock valuations with any third party.</span>
              </div>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">4. Security & Backups</h4>
              <p>
                All data is encrypted in transit via 256-bit TLS/HTTPS and backed up daily on encrypted AWS cloud infrastructure with strict multi-tier role-based access control (RBAC).
              </p>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">5. Contact Data Governance</h4>
              <p>
                Email: <a href="mailto:support@jewelbiz.com" class="gold-link">support@jewelbiz.com</a> | Phone: <a href="tel:+919633845820" class="gold-link">+91 9633845820</a><br />
                Kochi, Kerala, India.
              </p>
            </div>
          </div>

          <!-- TERMS & CONDITIONS CONTENT -->
          <div v-else class="legal-modal-inner">
            <div class="modal-legal-badge-row">
              <span class="meta-pill-sm"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-file-document-outline</v-icon> Commercial SaaS Agreement</span>
              <span class="meta-pill-sm"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-calendar-check</v-icon> Effective: Sept 2026</span>
              <span class="meta-pill-sm"><v-icon x-small color="#9E6D2B" class="mr-1">mdi-scale-balance</v-icon> Indian Commercial Laws</span>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">1. Acceptance & Service Scope</h4>
              <p>
                By accessing or subscribing to JewelBiz, you agree to these Terms. JewelBiz grants you a non-exclusive, non-transferable SaaS license for point-of-sale billing, inventory control, Karigar workshop management, and gold scheme tracking.
              </p>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">2. Daily Board Rate Responsibility</h4>
              <div class="modal-callout-box">
                <v-icon small color="#9E6D2B" class="mr-2">mdi-alert-circle-outline</v-icon>
                <span>Store administrators are responsible for verifying and locking daily showroom gold and silver board rates, making charges, and statutory tax percentages prior to customer billing.</span>
              </div>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">3. User Credentials & Roles</h4>
              <p>
                Subscribers are responsible for managing staff access roles, safeguarding login credentials, and immediately deactivating accounts for departing employees.
              </p>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">4. 99.9% Uptime SLA & Cloud Backups</h4>
              <p>
                We maintain a 99.9% availability target with automated daily offsite snapshots. Scheduled maintenance is performed during non-trading off-hours with advance notice.
              </p>
            </div>

            <div class="modal-legal-block">
              <h4 class="modal-section-h">5. Intellectual Property & Governing Law</h4>
              <p>
                All source code, platform architecture, designs, and branding are the proprietary property of JewelBiz. These terms are governed by the laws of India, with exclusive jurisdiction in Kochi, Kerala.
              </p>
            </div>
          </div>
        </div>

        <!-- Modal Footer Actions -->
        <div class="clean-legal-footer">
          <div class="d-flex align-center">
            <router-link :to="legalTab === 'privacy' ? '/privacy-policy' : '/terms-conditions'" class="modal-full-doc-link" target="_blank">
              Open Full Document in New Tab ↗
            </router-link>
          </div>
          <button type="button" class="btn-modal-close-gold" @click="legalDialog = false">
            I Understand & Close
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Success SnackBar -->
    <v-snackbar v-model="bookingSnackbar" color="#9E6D2B" timeout="4500" top>
      <span style="color: #FFFFFF; font-weight: 600;">
        {{ bookingMessage }}
      </span>
      <template v-slot:action="{ attrs }">
        <v-btn text color="#FFFFFF" v-bind="attrs" @click="bookingSnackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: "LandingPage",
  data() {
    return {
      isScrolled: false,
      mobileDrawer: false,
      bookDemoDialog: false,
      detailModal: false,
      legalDialog: false,
      legalTab: 'privacy',
      selectedItem: null,
      currentHeroSlide: 1,
      heroTimer: null,
      heroSlides: [
        {
          id: 1,
          image: "/landing/hero_emerald_jewellery.jpg",
          caption: "Emerald & Diamond Gold Jewellery",
          subcaption: "Crafted for Modern Jewellery Businesses"
        },
        {
          id: 2,
          image: "/landing/banner_wedding_bangles.jpg",
          caption: "Bridal Gold & Antique Bangles",
          subcaption: "High-Value Inventory & Valuation Management"
        },
        {
          id: 3,
          image: "/landing/collection_rings.jpg",
          caption: "Diamond Solitaires & Gold Rings",
          subcaption: "Precision Weight & Karigar Workshop Ledger"
        },
        {
          id: 4,
          image: "/landing/collection_mangalsutra.jpg",
          caption: "Traditional & Contemporary Gold Mangalsutra",
          subcaption: "Automated Schemes & Live Market Rates"
        },
        {
          id: 5,
          image: "/landing/collection_earrings.jpg",
          caption: "Diamond Studs & Designer Ornaments",
          subcaption: "HUID & GST Compliant Counter POS Invoicing"
        }
      ],

      demoFormValid: true,
      bookingLoading: false,
      bookingSnackbar: false,
      bookingMessage: "",
      todayDate: new Date().toISOString().split('T')[0],
      demoTimeSlots: [
        'Morning (9 AM - 12 PM)',
        'Afternoon (12 PM - 4 PM)',
        'Evening (4 PM - 7 PM)'
      ],
      demoInterestTags: [
        'POS & GST Billing',
        'Karigar Workshop Ledger',
        'Gold Savings Schemes',
        'Barcode & RFID Stock',
        'Multi-Branch Sync',
        'Old Gold Exchange'
      ],
      selectedInterestTags: [],
      booking: {
        name: "",
        jewelleryName: "",
        email: "",
        phone: "",
        preferredDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
        preferredTime: "Afternoon (12 PM - 4 PM)",
        notes: ""
      },

      // 8 Operational Pillars of JewelBiz (Using the luxury images)
      erpPillars: [
        {
          title: "POS & Billing",
          image: "/landing/circle_earrings.jpg",
          desc: "Lightning fast billing with stone deductions, old gold adjustments, and automated GST invoice printing."
        },
        {
          title: "Barcode Stock",
          image: "/landing/circle_rings.jpg",
          desc: "RFID and barcode tags with gross, stone, and net weight tracking for ring, bangles, and loose stones."
        },
        {
          title: "Daily Rates",
          image: "/landing/circle_pendants.jpg",
          desc: "Automated morning sync of 24K, 22K, 18K gold and silver market boarding rates across all terminals."
        },
        {
          title: "Karigar Ledger",
          image: "/landing/circle_mangalsutra.jpg",
          desc: "Raw metal issue, finished item receipts, melting wastage loss calculations, and craftsman ledgers."
        },
        {
          title: "Old Gold Aciding",
          image: "/landing/circle_bracelets.jpg",
          desc: "Customer old gold intake, purity verification, acid testing, and batch melting into 24K bullion."
        },
        {
          title: "Gold Schemes",
          image: "/landing/circle_necklaces.jpg",
          desc: "11-month and 12-month customer savings schemes with digital passbooks, SMS alerts, and bonus tracking."
        },
        {
          title: "Financial P&L",
          image: "/landing/circle_bangles.jpg",
          desc: "Double-entry accounting, Day Book, customer statements, supplier balances, Trial Balance, and Balance Sheet."
        },
        {
          title: "Security & Audit",
          image: "/landing/circle_nosepins.jpg",
          desc: "Role-based staff permissions, tamper-proof activity audit logs, and automatic secure cloud backups."
        }
      ],

      // 4 Main Feature Showcase Cards (Using the luxury collection images)
      erpModules: [
        {
          tag: "Sales & Billing",
          title: "Smart POS & GST Invoicing",
          subtitle: "Fast counter billing with old gold exchange, stone deductions & instant HUID compliant invoices",
          image: "/landing/collection_earrings.jpg",
          desc: "Process sales in seconds. Deduct old gold purchases seamlessly, record customer advances, apply approved discounts, and print branded GST invoices."
        },
        {
          tag: "Inventory & Barcoding",
          title: "Real-Time Stock & Valuation",
          subtitle: "Accurate gross, stone & net weight separation with real-time stock value calculations per branch",
          image: "/landing/collection_rings.jpg",
          desc: "Scan barcodes instantly. Maintain complete visibility over 18K, 22K, 24K gold, diamond pieces, and certified gemstones across all showcase counters."
        },
        {
          tag: "Artisan Workshop",
          title: "Karigar & Repair Tracking",
          subtitle: "Issue-receive registers, melting loss audit trails & customer repair job management",
          image: "/landing/collection_pendants.jpg",
          desc: "Dispatch pure metal or scrap to craftsmen with strict weight logging. Calculate exact melting loss fractions and track customer repairs with photo tags."
        },
        {
          tag: "Customer Investment",
          title: "Gold Savings Schemes",
          subtitle: "Automate 11/12 month installment tracking, customer ledger passbooks & maturity bonuses",
          image: "/landing/collection_mangalsutra.jpg",
          desc: "Secure recurring showroom revenue. Enroll customers into monthly jewellery savings plans, collect installments, and automate bonus payouts upon redemption."
        }
      ],

      // 4 "Why Choose JewelBiz" Cards
      whyCards: [
        {
          icon: "mdi-certificate-outline",
          title: "100% Tax & HUID Ready",
          desc: "Pre-configured for Indian GST rates, old gold tax offsets, and mandatory 6-digit BIS Hallmark HUID tracking."
        },
        {
          icon: "mdi-scale-balance",
          title: "Tight Karigar Accounting",
          desc: "Monitor raw gold dispatched to goldsmiths, track melting wastage fractions, and eliminate unexplained workshop loss."
        },
        {
          icon: "mdi-store-outline",
          title: "Multi-Counter & Multi-Branch",
          desc: "Manage multiple sales counters, vaults, and branch locations with role-based access for staff and managers."
        },
        {
          icon: "mdi-trending-up",
          title: "Live Boarding Rates Sync",
          desc: "Automatically sync 18K, 22K, 24K gold and silver market rates straight into your billing terminals every morning."
        }
      ],

      // Detailed Core Platform Features
      detailedFeatures: [
        {
          icon: "mdi-barcode-scan",
          title: "Instant Barcode & RFID",
          desc: "Generate custom barcode tags with gross, net, stone weight, and purity. Scan items on counters in under a second."
        },
        {
          icon: "mdi-sync",
          title: "Live Boarding Rate Sync",
          desc: "Sync official 24K, 22K, 18K gold and silver market rates across all billing terminals in real-time."
        },
        {
          icon: "mdi-hammer-wrench",
          title: "Karigar Workshop Audit",
          desc: "Dispatch raw gold to craftsmen, log repair jobs, and eliminate unauthorized melting loss."
        },
        {
          icon: "mdi-piggy-bank-outline",
          title: "Gold Schemes & Chits",
          desc: "Manage customer gold investment schemes with automated SMS notifications and maturity bonuses."
        },
        {
          icon: "mdi-receipt-text-check-outline",
          title: "HUID & GST Invoicing",
          desc: "Generate compliant GST bills, record old gold melting adjustments, and track customer ledger balances."
        },
        {
          icon: "mdi-chart-line",
          title: "Profit & Loss Analytics",
          desc: "Comprehensive balance sheets, trial balance, supplier settlements, and tax reports in one click."
        }
      ],

      // Frequently Asked Questions
      faqs: [
        {
          question: "How does JewelBiz help jewellery retail stores?",
          answer: "JewelBiz is an end-to-end Enterprise Resource Planning (ERP) software tailored specifically for jewellery retail showrooms and wholesalers. It covers POS billing, old gold exchange, barcode stock, karigar workshop tracking, gold investment schemes, and double-entry accounting."
        },
        {
          question: "Does JewelBiz support BIS Hallmark HUID tracking?",
          answer: "Yes. JewelBiz fully adheres to BIS Hallmarking guidelines with 6-digit alphanumeric HUID tracking per piece, ensuring easy verification, invoice compliance, and seamless government audits."
        },
        {
          question: "Can multiple branches and sales counters be managed simultaneously?",
          answer: "Absolutely. JewelBiz provides role-based access for SuperAdmins, Store Admins, and Counter Staff, allowing seamless multi-branch inventory transfers and unified centralized reporting."
        },
        {
          question: "How does old gold exchange billing work?",
          answer: "The POS billing module allows instantaneous entry of customer old gold, purity testing deductions, melting loss calculation, and automatic value offset against new ornament purchases."
        },
        {
          question: "Is customer data and financial data safe in JewelBiz?",
          answer: "All data is hosted on enterprise cloud servers with 256-bit encryption, automated daily offsite backups, and fine-grained staff permission controls."
        }
      ]
    };
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll);
    this.injectSEO();
    this.startHeroTimer();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
    this.stopHeroTimer();
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 20;
    },
    startHeroTimer() {
      this.stopHeroTimer();
      this.heroTimer = setInterval(() => {
        this.nextHeroSlide();
      }, 3000);
    },
    stopHeroTimer() {
      if (this.heroTimer) {
        clearInterval(this.heroTimer);
        this.heroTimer = null;
      }
    },
    pauseHeroTimer() {
      this.stopHeroTimer();
    },
    goToHeroSlide(slideNum) {
      this.currentHeroSlide = slideNum;
    },
    prevHeroSlide() {
      const len = this.heroSlides.length;
      this.currentHeroSlide = this.currentHeroSlide > 1 ? this.currentHeroSlide - 1 : len;
    },
    nextHeroSlide() {
      const len = this.heroSlides.length;
      this.currentHeroSlide = this.currentHeroSlide < len ? this.currentHeroSlide + 1 : 1;
    },
    openModuleDetail(item) {
      this.selectedItem = item;
      this.detailModal = true;
    },
    injectSEO() {
      document.title = "JewelBiz - Smart Jewellery Business Management ERP";
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement('meta');
        metaDesc.name = "description";
        document.head.appendChild(metaDesc);
      }
      metaDesc.content = "Streamline your jewellery business with JewelBiz ERP. Features include POS billing, stock valuation, karigar tracking, gold schemes, and live market rates.";
    },
    isDemoTagActive(tag) {
      return this.selectedInterestTags.includes(tag);
    },
    toggleDemoTag(tag) {
      const idx = this.selectedInterestTags.indexOf(tag);
      if (idx > -1) {
        this.selectedInterestTags.splice(idx, 1);
      } else {
        this.selectedInterestTags.push(tag);
      }
      this.syncDemoNotesWithTags();
    },
    syncDemoNotesWithTags() {
      if (this.selectedInterestTags.length > 0) {
        const tagText = "Interested in: " + this.selectedInterestTags.join(", ");
        const existingCustomNote = (this.booking.notes || "")
          .replace(/^Interested in: .*?\n?/, "")
          .trim();
        this.booking.notes = existingCustomNote
          ? tagText + "\n" + existingCustomNote
          : tagText;
      }
    },
    submitBooking() {
      if (this.$refs.demoForm.validate()) {
        this.bookingLoading = true;
        axios({
          url: "/user/bookdemo",
          method: "POST",
          data: this.booking
        })
          .then((response) => {
            this.bookingLoading = false;
            if (response.data.status === true) {
              this.bookingMessage = response.data.msg || "Demo request submitted successfully! Our team will contact you.";
              this.bookingSnackbar = true;
              this.bookDemoDialog = false;
              this.selectedInterestTags = [];
              this.booking = {
                name: "",
                jewelleryName: "",
                email: "",
                phone: "",
                preferredDate: new Date(Date.now() + 86400000).toISOString().split('T')[0],
                preferredTime: "Afternoon (12 PM - 4 PM)",
                notes: ""
              };
              if (this.$refs.demoForm) this.$refs.demoForm.resetValidation();
            } else {
              this.bookingMessage = response.data.msg || "Failed to submit booking.";
              this.bookingSnackbar = true;
            }
          })
          .catch((error) => {
            this.bookingLoading = false;
            console.error("Error booking demo:", error);
            this.bookingMessage = "Demo request received! Our team will contact you shortly.";
            this.bookingSnackbar = true;
            this.bookDemoDialog = false;
          });
      }
    },
    openLegalModal(tab) {
      this.legalTab = tab || 'privacy';
      this.legalDialog = true;
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;600;700;800&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,400;1,500;1,600&family=Marcellus&family=Plus+Jakarta+Sans:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400;1,500&display=swap');

/* ==========================================================================
   BRIGHT LUXURY JEWELBIZ ERP STYLESHEET
   Matching reference aesthetic: warm ivory (#FAF8F5), caramel gold (#9E6D2B),
   refined luxury serif headlines ('Cormorant Garamond'), and high aesthetics.
   ========================================================================== */

.landing-page-bright {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  background-color: #FAF8F5;
  color: #1E1815;
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/* 1. MAIN HEADER */
.main-header {
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  border-bottom: 1px solid #ECE3D5;
  z-index: 100;
  transition: all 0.3s ease;
  padding: 16px 5%;
}

.main-header.scrolled {
  box-shadow: 0 4px 20px rgba(60, 40, 15, 0.06);
  background-color: #FFFFFF;
  padding: 12px 5%;
}

.header-inner {
  max-width: 1320px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.brand-logo-link {
  display: flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.logo-mark {
  width: 38px;
  height: 38px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.diamond-svg {
  width: 100%;
  height: 100%;
  filter: drop-shadow(0 2px 4px rgba(158, 109, 43, 0.18));
}

.logo-text-block {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.brand-name {
  font-family: 'Cinzel', 'Cormorant Garamond', Georgia, serif;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 2.2px;
  color: #1E1815;
  line-height: 1.1;
  text-align: left;
}

.brand-highlight {
  color: #9E6D2B;
}

.brand-subtext {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 8.5px;
  letter-spacing: 2px;
  font-weight: 600;
  color: #8C7D6F;
  text-transform: uppercase;
  margin-top: 3px;
  text-align: left;
}

.desktop-nav {
  display: flex;
  align-items: center;
  gap: 32px;
}

.nav-link {
  color: #2F2620;
  text-decoration: none;
  font-size: 14.5px;
  font-weight: 500;
  transition: all 0.2s ease;
  position: relative;
  padding: 6px 0;
}

.nav-link:hover {
  color: #9E6D2B;
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 0%;
  height: 2px;
  background-color: #9E6D2B;
  transition: width 0.25s ease;
}

.nav-link:hover::after {
  width: 100%;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 14px;
}

.btn-demo-outline {
  border: 1.5px solid #9E6D2B;
  color: #9E6D2B;
  background: transparent;
  padding: 9px 20px;
  border-radius: 50px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.btn-demo-outline:hover {
  background-color: #FAF4EB;
  box-shadow: 0 4px 12px rgba(158, 109, 43, 0.12);
  transform: translateY(-1px);
}

.btn-icon {
  color: #9E6D2B !important;
}

.btn-signin-gold {
  background: linear-gradient(135deg, #A4712E 0%, #8E5E1D 100%);
  color: #FFFFFF !important;
  border: none;
  padding: 9px 22px;
  border-radius: 50px;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  box-shadow: 0 4px 14px rgba(158, 109, 43, 0.25);
}

.btn-signin-gold:hover {
  background: linear-gradient(135deg, #B57F34 0%, #9B6925 100%);
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.35);
  transform: translateY(-1px);
}

.btn-signin-gold .btn-icon {
  color: #FFFFFF !important;
}

.mobile-toggle-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}

.mobile-nav-links {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.mobile-nav-links a {
  font-size: 16px;
  font-weight: 600;
  color: #2F2620;
  text-decoration: none;
}

/* 2. HERO SHOWCASE SECTION */
.hero-showcase {
  background: radial-gradient(circle at 80% 30%, rgba(245, 235, 218, 0.6) 0%, rgba(250, 248, 245, 1) 70%);
  padding: 44px 5% 60px 5%;
  position: relative;
}

.hero-container {
  max-width: 1320px;
  margin: 0 auto;
}

.hero-grid {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  align-items: center;
  gap: 48px;
}

.hero-text-side {
  text-align: left;
}

.eyebrow-line {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.eyebrow-text {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 2.4px;
  color: #9E6D2B;
  text-transform: uppercase;
}

.eyebrow-bar {
  width: 50px;
  height: 1.5px;
  background-color: #9E6D2B;
}

.hero-headline {
  font-family: 'Cormorant Garamond', 'Marcellus', Garamond, Georgia, serif;
  font-size: 60px;
  line-height: 1.12;
  color: #1E1815;
  font-weight: 600;
  letter-spacing: -0.3px;
  margin-bottom: 20px;
}

.headline-serif-gold {
  font-family: 'Cormorant Garamond', 'Marcellus', Garamond, Georgia, serif;
  color: #A2722C;
  font-weight: 500;
  font-style: normal;
  display: inline-block;
}

.hero-subline {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15.5px;
  line-height: 1.7;
  color: #5C5248;
  max-width: 520px;
  margin-bottom: 32px;
  font-weight: 400;
}

.hero-button-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 36px;
}

.hero-btn-primary {
  background: linear-gradient(135deg, #A4712E 0%, #905F1E 100%);
  color: #FFFFFF;
  border: none;
  padding: 14px 30px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.28);
  display: inline-flex;
  align-items: center;
  gap: 8px;
  text-decoration: none;
}

.hero-btn-primary:hover {
  background: linear-gradient(135deg, #B57F34 0%, #9B6925 100%);
  box-shadow: 0 8px 24px rgba(158, 109, 43, 0.38);
  transform: translateY(-2px);
}

.arrow-symbol {
  font-size: 18px;
  transition: transform 0.2s ease;
}

.hero-btn-primary:hover .arrow-symbol {
  transform: translateX(4px);
}

.hero-btn-outline {
  background: #FFFFFF;
  color: #5C431E;
  border: 1.5px solid #D1B896;
  padding: 13px 26px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
}

.hero-btn-outline:hover {
  border-color: #9E6D2B;
  background-color: #FAF4EB;
  transform: translateY(-2px);
}

.play-icon-circle {
  font-size: 12px;
  color: #9E6D2B;
}

.hero-trust-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 24px;
  padding-top: 10px;
  border-top: 1px solid #EFE8DD;
}

.trust-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.trust-icon {
  color: #9E6D2B !important;
}

.trust-label {
  font-size: 13px;
  font-weight: 600;
  color: #4A4138;
}

/* Right Visual Side */
.hero-visual-side {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.hero-visual-card {
  position: relative;
  width: 100%;
  height: 410px;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 16px 40px rgba(74, 52, 23, 0.12);
  border: 1px solid rgba(230, 215, 194, 0.8);
  background-color: #FAF4EB;
}

.hero-slide-item {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  visibility: hidden;
  transform: scale(1.03);
  transition: opacity 1.4s cubic-bezier(0.25, 1, 0.5, 1),
              transform 1.4s cubic-bezier(0.25, 1, 0.5, 1),
              visibility 1.4s ease;
  will-change: opacity, transform;
  z-index: 1;
}

.hero-slide-item.active {
  opacity: 1;
  visibility: visible;
  transform: scale(1);
  z-index: 2;
}

.hero-showcase-img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
  object-position: center;
}

.hero-visual-card:hover .hero-showcase-img {
  transform: scale(1.03);
}

.crafted-caption {
  position: absolute;
  bottom: 20px;
  right: 24px;
  z-index: 5;
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 15px;
  font-style: italic;
  font-weight: 500;
  color: #7A5B2B;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(8px);
  padding: 6px 16px;
  border-radius: 30px;
  border: 1px solid rgba(220, 195, 160, 0.6);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.5s ease;
}

.caption-line {
  width: 24px;
  height: 1px;
  background-color: #9E6D2B;
}

.hero-carousel-nav {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 18px;
}

.carousel-arrow {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #DFD5C6;
  background: #FFFFFF;
  color: #6B5D4E;
  font-size: 18px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1;
}

.carousel-arrow:hover {
  background-color: #FAF2E6;
  border-color: #9E6D2B;
  color: #9E6D2B;
}

.carousel-dots {
  display: flex;
  gap: 8px;
}

.carousel-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #DFD5C6;
  cursor: pointer;
  transition: all 0.25s ease;
}

.carousel-dot.active {
  width: 24px;
  border-radius: 10px;
  background-color: #9E6D2B;
}

/* 3. CATEGORY CIRCLES SECTION */
.category-circles-section {
  padding: 40px 5% 50px 5%;
  background-color: #FAF8F5;
  border-bottom: 1px solid #EFE8DD;
}

.circles-container {
  max-width: 1320px;
  margin: 0 auto;
}

.circles-track {
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow-x: auto;
  gap: 20px;
  padding: 10px 4px 20px 4px;
  scrollbar-width: thin;
}

.circle-card-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  flex-shrink: 0;
  transition: transform 0.25s ease;
}

.circle-card-item:hover {
  transform: translateY(-4px);
}

.circle-avatar-frame {
  width: 86px;
  height: 86px;
  border-radius: 50%;
  padding: 4px;
  background: #FFFFFF;
  border: 1.5px solid #E8DECF;
  box-shadow: 0 4px 14px rgba(74, 52, 23, 0.06);
  transition: all 0.25s ease;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.circle-card-item:hover .circle-avatar-frame {
  border-color: #9E6D2B;
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.22);
}

.circle-item-img {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.circle-card-item:hover .circle-item-img {
  transform: scale(1.08);
}

.circle-item-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 15.5px;
  font-weight: 600;
  color: #2E2620;
  text-align: center;
  letter-spacing: 0.2px;
  transition: color 0.25s ease;
}

.circle-card-item:hover .circle-item-title {
  color: #9E6D2B;
}

/* 4. EXCLUSIVE MODULES (4 CARDS) */
.exclusive-collections-section {
  padding: 70px 5% 80px 5%;
  background-color: #FAF8F5;
  position: relative;
}

.section-header-wrap {
  max-width: 1320px;
  margin: 0 auto 36px auto;
  position: relative;
}

.section-kicker {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 2.4px;
  color: #9E6D2B;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.section-main-title {
  font-family: 'Cormorant Garamond', 'Marcellus', Garamond, Georgia, serif;
  font-size: 42px;
  font-weight: 600;
  color: #1E1815;
  line-height: 1.18;
  letter-spacing: -0.3px;
}

.section-desc {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  color: #6E6256;
  margin-top: 8px;
  line-height: 1.6;
}

.view-all-link {
  position: absolute;
  top: 10px;
  right: 0;
  font-size: 14px;
  font-weight: 600;
  color: #9E6D2B;
  text-decoration: none;
  transition: all 0.2s ease;
}

.view-all-link:hover {
  text-decoration: underline;
  transform: translateX(3px);
}

.collections-grid-container {
  max-width: 1320px;
  margin: 0 auto;
}

.collections-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.collection-card {
  background-color: #FFFFFF;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid #EFE7DA;
  box-shadow: 0 6px 20px rgba(60, 40, 15, 0.05);
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
}

.collection-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 32px rgba(60, 40, 15, 0.12);
  border-color: #DFCFBA;
}

.collection-img-wrap {
  position: relative;
  width: 100%;
  padding-top: 100%; /* 1:1 Aspect Ratio */
  overflow: hidden;
  background-color: #F8F4EE;
}

.collection-img {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.collection-card:hover .collection-img {
  transform: scale(1.06);
}

.collection-tag {
  position: absolute;
  top: 12px;
  left: 12px;
  background: rgba(255, 255, 255, 0.92);
  backdrop-filter: blur(6px);
  color: #9E6D2B;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(220, 200, 175, 0.6);
}

.collection-card-body {
  padding: 18px 16px 20px 16px;
  text-align: center;
  background-color: #FFFFFF;
}

.collection-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 22px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 6px;
  letter-spacing: -0.1px;
}

.collection-subtitle {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  line-height: 1.55;
  color: #73675C;
  margin: 0;
}

/* 5. WHY CHOOSE JEWELBIZ (4 WHITE CARDS) */
.why-choose-section {
  padding: 70px 5% 80px 5%;
  background-color: #FAF8F5;
  border-top: 1px solid #EFE8DD;
}

.why-cards-container {
  max-width: 1320px;
  margin: 0 auto;
}

.why-cards-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
}

.why-card {
  background-color: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 18px;
  padding: 32px 24px;
  text-align: center;
  box-shadow: 0 6px 18px rgba(60, 40, 15, 0.04);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.why-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 28px rgba(60, 40, 15, 0.08);
  border-color: #DFCFBA;
}

.why-icon-box {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background-color: #FAF4EB;
  border: 1px solid #EFE4D3;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  transition: background-color 0.25s ease;
}

.why-card:hover .why-icon-box {
  background-color: #F4E8D4;
}

.why-gold-icon {
  color: #9E6D2B !important;
}

.why-card-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 21px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 8px;
}

.why-card-desc {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px;
  line-height: 1.6;
  color: #6E6358;
  margin: 0;
}

/* 6. OCCASION BANNER */
.occasion-banner-section {
  padding: 20px 5% 70px 5%;
  background-color: #FAF8F5;
}

.occasion-banner-wrapper {
  max-width: 1320px;
  margin: 0 auto;
}

.occasion-banner-inner {
  background: #F7EFE4;
  border-radius: 20px;
  border: 1px solid #EADBCC;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  align-items: center;
  box-shadow: 0 10px 30px rgba(80, 50, 20, 0.07);
}

.occasion-text-wrap {
  padding: 48px 48px;
  text-align: left;
}

.occasion-heading {
  font-family: 'Cormorant Garamond', 'Marcellus', Garamond, Georgia, serif;
  font-size: 38px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 12px;
  line-height: 1.2;
  letter-spacing: -0.3px;
}

.occasion-subtext {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15px;
  color: #635649;
  line-height: 1.65;
  max-width: 480px;
  margin-bottom: 28px;
}

.occasion-cta-btn {
  background: linear-gradient(135deg, #A4712E 0%, #905F1E 100%);
  color: #FFFFFF;
  border: none;
  padding: 13px 28px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.28);
}

.occasion-cta-btn:hover {
  background: linear-gradient(135deg, #B57F34 0%, #9B6925 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 22px rgba(158, 109, 43, 0.38);
}

.occasion-img-wrap {
  width: 100%;
  height: 100%;
  min-height: 280px;
  overflow: hidden;
}

.occasion-bangles-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.6s ease;
}

.occasion-banner-inner:hover .occasion-bangles-img {
  transform: scale(1.04);
}

/* 7. DEEP FEATURES SECTION */
.deep-features-section {
  padding: 70px 5% 80px 5%;
  background-color: #FAF8F5;
  border-top: 1px solid #EFE8DD;
}

.features-grid-container {
  max-width: 1320px;
  margin: 0 auto;
}

.features-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}

.feature-tile {
  background-color: #FFFFFF;
  border: 1px solid #EFE8DD;
  border-radius: 16px;
  padding: 28px 24px;
  box-shadow: 0 4px 14px rgba(60, 40, 15, 0.03);
  transition: all 0.25s ease;
}

.feature-tile:hover {
  border-color: #DFCFBA;
  box-shadow: 0 10px 22px rgba(60, 40, 15, 0.08);
  transform: translateY(-3px);
}

.feature-tile-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background-color: #FAF4EB;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
}

.feature-tile-icon {
  color: #9E6D2B !important;
}

.feature-tile-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 20px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 8px;
}

.feature-tile-desc {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13.5px;
  line-height: 1.65;
  color: #695E54;
  margin: 0;
}

/* 8 & 9. SPOTLIGHT SECTIONS */
.spotlight-section {
  padding: 80px 5%;
  background-color: #FAF8F5;
}

.spotlight-section.bg-alt {
  background-color: #F6F1EA;
}

.spotlight-container {
  max-width: 1320px;
  margin: 0 auto;
}

.spotlight-grid {
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: center;
  gap: 56px;
}

.spotlight-grid.reverse-on-desktop {
  grid-template-columns: 0.9fr 1.1fr;
}

.spotlight-paragraph {
  font-size: 15px;
  line-height: 1.7;
  color: #5E5348;
  margin: 16px 0 24px 0;
}

.spotlight-list {
  list-style: none;
  padding: 0;
  margin: 0 0 24px 0;
}

.spotlight-list li {
  font-size: 14px;
  font-weight: 600;
  color: #383028;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
}

.check-icon {
  color: #9E6D2B !important;
}

.spotlight-visual {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 14px 34px rgba(74, 52, 23, 0.1);
  border: 1px solid #E8DECF;
}

.spotlight-img {
  width: 100%;
  height: 380px;
  object-fit: cover;
  display: block;
}

/* 10. FAQ SECTION */
.faq-bright-section {
  padding: 80px 5%;
  background-color: #FAF8F5;
  border-top: 1px solid #EFE8DD;
}

.faq-container {
  max-width: 880px;
  margin: 0 auto;
}

.faq-panels-bright {
  background: transparent !important;
}

.faq-panel-bright {
  background-color: #FFFFFF !important;
  border: 1px solid #EFE8DD !important;
  border-radius: 14px !important;
  margin-bottom: 14px !important;
  box-shadow: 0 4px 12px rgba(60, 40, 15, 0.03) !important;
  overflow: hidden;
}

.faq-header-bright {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 20px;
  font-weight: 600;
  color: #1E1815;
  padding: 20px 24px;
}

.faq-content-bright {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 14.5px;
  line-height: 1.7;
  color: #63574C;
  padding: 0 24px 20px 24px;
}

/* 11. BOTTOM CALLOUT SECTION */
.bottom-callout-section {
  padding: 40px 5% 80px 5%;
  background-color: #FAF8F5;
}

.callout-card {
  max-width: 1100px;
  margin: 0 auto;
  background: linear-gradient(135deg, #FBF6EF 0%, #F5ECDF 100%);
  border: 1px solid #E7DAC9;
  border-radius: 24px;
  padding: 60px 40px;
  text-align: center;
  box-shadow: 0 12px 36px rgba(80, 50, 20, 0.08);
}

.callout-ornament {
  color: #9E6D2B;
  font-size: 24px;
  margin-bottom: 12px;
}

.callout-title {
  font-family: 'Cormorant Garamond', 'Marcellus', Garamond, Georgia, serif;
  font-size: 42px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 14px;
  letter-spacing: -0.3px;
}

.callout-desc {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 15.5px;
  color: #665A4F;
  max-width: 640px;
  margin: 0 auto 32px auto;
  line-height: 1.65;
}

.callout-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  flex-wrap: wrap;
}

.btn-callout-primary {
  background: linear-gradient(135deg, #A4712E 0%, #905F1E 100%);
  color: #FFFFFF;
  border: none;
  padding: 14px 32px;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.28);
}

.btn-callout-primary:hover {
  background: linear-gradient(135deg, #B57F34 0%, #9B6925 100%);
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(158, 109, 43, 0.38);
}

.btn-callout-secondary {
  background: #FFFFFF;
  color: #5C431E;
  border: 1.5px solid #D1B896;
  padding: 13px 28px;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.25s ease;
}

.btn-callout-secondary:hover {
  background-color: #FAF4EB;
  border-color: #9E6D2B;
  transform: translateY(-2px);
}

/* 12. BRIGHT FOOTER */
.bright-footer {
  background-color: #F4ECE1;
  border-top: 1px solid #E5D7C5;
  padding: 60px 5% 30px 5%;
  color: #574B40;
}

.footer-container {
  max-width: 1320px;
  margin: 0 auto;
}

.footer-top-row {
  display: grid;
  grid-template-columns: 1.4fr 0.9fr 0.9fr 0.9fr 1.1fr;
  gap: 32px;
  padding-bottom: 40px;
  border-bottom: 1px solid #E8DCCB;
}

.brand-col {
  padding-right: 16px;
}

.footer-brand-desc {
  font-size: 13.5px;
  line-height: 1.65;
  color: #6E6154;
}

.footer-col-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 20px;
  font-weight: 600;
  color: #1E1815;
  margin-bottom: 18px;
  letter-spacing: 0.2px;
}

.footer-links {
  list-style: none;
  padding: 0;
  margin: 0;
}

.footer-links li {
  margin-bottom: 10px;
}

.footer-links a,
.footer-link-action {
  color: #6E6154;
  text-decoration: none;
  font-size: 13.5px;
  transition: color 0.2s ease;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}

.footer-links a:hover,
.footer-link-action:hover {
  color: #9E6D2B;
  text-decoration: underline;
}

.contact-links li {
  display: flex;
  align-items: center;
  font-size: 13.5px;
  color: #6E6154;
}

.footer-contact-icon {
  color: #9E6D2B !important;
}

.btn-sm-footer {
  padding: 6px 14px;
  font-size: 12px;
}

.footer-bottom-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding-top: 24px;
  font-size: 12.5px;
  color: #7D7062;
}

.footer-legal-bar-links {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.footer-legal-btn {
  background: none;
  border: none;
  padding: 0;
  color: #6E6154;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-legal-btn:hover {
  color: #9E6D2B;
  text-decoration: underline;
}

.legal-dot {
  color: #C5BAAD;
  font-size: 12px;
}

.footer-bottom-badges {
  display: flex;
  gap: 16px;
}

.cert-badge {
  font-size: 11.5px;
  color: #9E6D2B;
  font-weight: 600;
}

/* ==========================================================================
   LUXURY LEGAL MODAL (PRIVACY POLICY & TERMS)
   ========================================================================== */
.luxury-legal-dialog {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 24px 60px -10px rgba(35, 25, 18, 0.35) !important;
  background: transparent !important;
}

.clean-legal-card {
  background: #FFFFFF;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(158, 109, 43, 0.16);
  display: flex;
  flex-direction: column;
  max-height: 85vh;
}

.clean-legal-header {
  padding: 24px 28px 16px 28px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  border-bottom: 1px solid #F0EAE1;
  background: #FAF8F5;
}

.clean-legal-title {
  font-family: 'Cinzel', serif;
  font-size: 24px;
  font-weight: 700;
  color: #1E1815;
  line-height: 1.2;
  margin-bottom: 4px;
}

.clean-legal-subtitle {
  font-size: 13px;
  color: #6E6154;
  margin: 0;
  line-height: 1.5;
}

.legal-tab-switcher {
  display: flex;
  background: #F3ECE2;
  padding: 6px;
  gap: 8px;
  border-bottom: 1px solid #EAE0D3;
}

.legal-tab-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 16px;
  border-radius: 10px;
  border: none;
  background: transparent;
  font-size: 13.5px;
  font-weight: 600;
  color: #6B5E55;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
}

.legal-tab-btn.active {
  background: #9E6D2B;
  color: #FFFFFF;
  box-shadow: 0 4px 12px rgba(158, 109, 43, 0.25);
}

.legal-modal-scroll-area {
  padding: 24px 28px;
  overflow-y: auto;
  max-height: 52vh;
}

.legal-modal-inner {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.modal-legal-badge-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 6px;
}

.meta-pill-sm {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  background: #FAF7F2;
  border: 1px solid rgba(158, 109, 43, 0.2);
  border-radius: 15px;
  font-size: 11px;
  font-weight: 600;
  color: #554438;
}

.modal-legal-block {
  border-bottom: 1px solid #F5EFE6;
  padding-bottom: 16px;
}

.modal-legal-block:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.modal-section-h {
  font-family: 'Cinzel', serif;
  font-size: 15px;
  font-weight: 700;
  color: #1E1815;
  margin-bottom: 8px;
}

.modal-legal-block p {
  font-size: 13.5px;
  line-height: 1.7;
  color: #4A3E36;
  margin-bottom: 0;
}

.modal-legal-list {
  padding-left: 18px;
  margin: 8px 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.modal-legal-list li {
  font-size: 13px;
  line-height: 1.65;
  color: #4A3E36;
}

.modal-callout-box {
  background: #FAF5ED;
  border-left: 3.5px solid #9E6D2B;
  border-radius: 6px;
  padding: 10px 14px;
  margin-top: 8px;
  font-size: 13px;
  color: #5A3F1A;
  display: flex;
  align-items: center;
}

.gold-link {
  color: #9E6D2B;
  font-weight: 600;
  text-decoration: none;
}

.clean-legal-footer {
  padding: 16px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid #F0EAE1;
  background: #FAF8F5;
  flex-wrap: wrap;
  gap: 12px;
}

.modal-full-doc-link {
  font-size: 13px;
  font-weight: 600;
  color: #9E6D2B;
  text-decoration: none;
}

.modal-full-doc-link:hover {
  text-decoration: underline;
}

.btn-modal-close-gold {
  padding: 8px 20px;
  background: linear-gradient(135deg, #B27E36 0%, #8E5E20 100%);
  color: #FFFFFF;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(158, 109, 43, 0.22);
  transition: all 0.2s ease;
}

.btn-modal-close-gold:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(158, 109, 43, 0.32);
}

/* MODALS & FORMS */
.bright-modal-glass {
  border-radius: 20px !important;
  overflow: hidden;
}

.bright-modal-card {
  background-color: #FFFFFF !important;
  border-radius: 20px;
}

/* ==========================================================================
   LUXURY SPLIT-SCREEN DEMO MODAL
   ========================================================================== */
.luxury-demo-dialog-clean {
  border-radius: 20px !important;
  overflow: hidden !important;
  box-shadow: 0 20px 50px -10px rgba(35, 25, 18, 0.3) !important;
  background: transparent !important;
}

.clean-demo-card {
  background: #FFFFFF;
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid rgba(158, 109, 43, 0.16);
  position: relative;
}

.gold-top-accent {
  height: 4px;
  background: linear-gradient(90deg, #8A591B 0%, #D4AF37 50%, #8A591B 100%);
  width: 100%;
}

.clean-modal-header {
  padding: 24px 28px 12px 28px;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.clean-gold-kicker {
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.5px;
  color: #9E6D2B;
  margin-bottom: 4px;
}

.clean-modal-title {
  font-family: 'Cormorant Garamond', Garamond, Georgia, serif;
  font-size: 28px;
  font-weight: 600;
  color: #1E1815;
  letter-spacing: -0.2px;
  line-height: 1.15;
  margin-bottom: 4px;
}

.clean-modal-subtitle {
  font-size: 13px;
  color: #73675C;
  margin: 0;
}

.clean-close-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  background: #F6F1EA;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  margin-left: 12px;
}

.clean-close-btn:hover {
  background: #ECE1D1;
}

.clean-modal-body {
  padding: 12px 28px 24px 28px;
}

.clean-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 8px;
}

.clean-form-group {
  margin-bottom: 4px;
}

.clean-field-label {
  font-size: 12px;
  font-weight: 600;
  color: #4A4035;
  display: block;
  margin-bottom: 4px;
}

.required-star {
  color: #C53030;
  margin-left: 2px;
}

.clean-field-input >>> .v-input__slot {
  background-color: #FAF8F5 !important;
  border-color: #E6DCCF !important;
  border-radius: 8px !important;
  min-height: 40px !important;
  transition: all 0.2s ease;
}

.clean-field-input >>> .v-input__slot:hover {
  border-color: #9E6D2B !important;
}

.clean-field-input.v-input--is-focused >>> .v-input__slot {
  border-color: #9E6D2B !important;
  box-shadow: 0 0 0 3px rgba(158, 109, 43, 0.12) !important;
}

.clean-field-input >>> input,
.clean-field-input >>> .v-select__selection,
.clean-field-input >>> textarea {
  font-size: 13px !important;
  color: #1E1815 !important;
}

.clean-submit-btn {
  width: 100%;
  padding: 13px 20px;
  margin-top: 14px;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #A4712E 0%, #8A591B 100%);
  color: #FFFFFF;
  font-size: 14.5px;
  font-weight: 600;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(158, 109, 43, 0.28);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.clean-submit-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 8px 22px rgba(158, 109, 43, 0.36);
}

.clean-submit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.clean-btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #FFFFFF;
  border-radius: 50%;
  animation: cleanSpin 0.8s linear infinite;
}

@keyframes cleanSpin {
  to { transform: rotate(360deg); }
}

@media (max-width: 600px) {
  .clean-form-grid {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .clean-modal-header,
  .clean-modal-body {
    padding-left: 20px;
    padding-right: 20px;
  }
}

.detail-modal-img-wrap {
  width: 100%;
  height: 240px;
  border-radius: 14px;
  overflow: hidden;
  background-color: #F8F4EE;
}

.detail-modal-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.detail-modal-desc {
  font-size: 14.5px;
  line-height: 1.6;
  color: #5E5246;
}

/* RESPONSIVE MEDIA QUERIES */
@media (max-width: 1024px) {
  .hero-grid {
    grid-template-columns: 1fr;
    gap: 36px;
  }
  .hero-visual-card {
    height: 360px;
  }
  .hero-headline {
    font-size: 42px;
  }
  .collections-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .why-cards-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .features-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .footer-top-row {
    grid-template-columns: 1fr 1fr;
  }
}

@media (max-width: 768px) {
  .hero-visual-card {
    height: 280px;
  }
  .occasion-banner-inner {
    grid-template-columns: 1fr;
  }
  .occasion-text-wrap {
    padding: 32px 24px;
  }
  .spotlight-grid,
  .spotlight-grid.reverse-on-desktop {
    grid-template-columns: 1fr;
    gap: 32px;
  }
  .spotlight-img {
    height: 260px;
  }
  .features-grid {
    grid-template-columns: 1fr;
  }
  .hero-headline {
    font-size: 34px;
  }
  .section-main-title {
    font-size: 28px;
  }
  .view-all-link {
    position: static;
    display: inline-block;
    margin-top: 10px;
  }
}

@media (max-width: 540px) {
  .hero-visual-card {
    height: 220px;
  }
  .collections-grid {
    grid-template-columns: 1fr;
  }
  .why-cards-grid {
    grid-template-columns: 1fr;
  }
  .footer-top-row {
    grid-template-columns: 1fr;
  }
}
</style>
